import { Injectable, inject } from '@angular/core';
import {
  auditTime,
  distinctUntilChanged,
  filter,
  Observable,
  switchMap,
} from 'rxjs';
import { Page, PageType, ParamsModel } from '@boels/models';
import { HttpService, UrlService } from '@boels/helpers';
import { ApiContextService } from '../../services/api-context/api-context.service';

@Injectable({
  providedIn: 'any',
})
export class CmsGatewayService {
  private readonly httpService = inject(HttpService);
  private urlService = inject(UrlService);
  private apiContextService = inject(ApiContextService);

  deepEqual(a: any[], b: any[]): boolean {
    if (a === b) return true;

    if (a == null || typeof a != 'object' || b == null || typeof b != 'object')
      return false;

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!keysB.includes(key) || !this.deepEqual(a[key as any], b[key as any]))
        return false;
    }

    return true;
  }

  resolvePageType(pageType: PageType, label: string): string {
    switch (pageType) {
      case PageType.CONTENT_PAGE:
        return `?pageLabelOrId=${label}`;
      case PageType.CATEGORY_PAGE:
        return `?fields=FULL&code=${label}`;
      case PageType.PRODUCT_PAGE:
        return `?fields=FULL&code=${label}`;
      default:
        return ``;
    }
  }

  compareContext(prevContext: any[], currentContext: any[]): boolean {
    return (
      this.deepEqual(prevContext[0], currentContext[0]) &&
      this.deepEqual(prevContext[1], currentContext[1])
    );
  }

  loadPage(label: string, pageType: PageType): Observable<Page> {
    if (!label.includes('cx-preview')) {
      if (label.includes('?')) {
        label = label.substring(0, label.indexOf('?'));
      }

      label = label?.replace(/^\/?[a-z]{2}-[a-z]{2}/, '');
      const isHomePage = ['homepage', '/'].includes(label);
      label = isHomePage ? '' : this.resolvePageType(pageType, label);
    } else {
      label = '';
    }
    return this.apiContextService.getContext().pipe(
      distinctUntilChanged(this.compareContext),
      auditTime(150),
      filter(([baseSite]) => !!baseSite),
      switchMap(([basSite, language, currency]) => {
        const queryParams: ParamsModel = [
          language ? ['lang', language] : [],
          currency ? ['curr', currency] : [],
          pageType ? ['pageType', pageType] : [],
        ];
        const url =
          this.urlService.combineUri(basSite.uid, 'cms', 'pages') + label;
        return this.httpService.get<Page>(url, queryParams);
      }),
    );
  }
}
